import React from "react";
import { useQuery } from "@tanstack/react-query";
import pushNotificationService from "../../../service/pushNotificationService";
import { Dialog, DialogContent, IconButton, CircularProgress } from "@mui/material";
import { Close, CheckCircle, Error, Cancel } from "@mui/icons-material";

export default function NotificationAnalyticsModal({ isOpen, onClose, notification }) {
  const notificationId = notification?.id || notification?._id;

  const { data: analyticsData, isLoading, error } = useQuery({
    queryKey: ["notification-analytics", notificationId],
    queryFn: () => pushNotificationService.getAnalytics(notificationId),
    enabled: !!isOpen && !!notificationId,
  });

  const analytics = analyticsData?.data?.data || analyticsData?.data || {};
  const counts = analytics?.counts || { SENT: 0, FAILED: 0, DEAD: 0 };
  const failures = analytics?.failures?.docs || [];

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: "16px", padding: "24px" } }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-semibold mb-0" style={{ fontSize: "20px", color: "#0B1C30" }}>
            Push Delivery Analytics
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
            {notification?.title}
          </p>
        </div>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </div>

      <DialogContent className="p-0">
        {isLoading ? (
          <div className="text-center py-5">
            <CircularProgress size={32} />
            <p className="text-muted mt-2" style={{ fontSize: "13px" }}>Loading delivery stats...</p>
          </div>
        ) : error ? (
          <div className="alert alert-warning my-3" style={{ fontSize: "13px" }}>
            Analytics data for this notification is being processed or unavailable.
          </div>
        ) : (
          <div className="py-2">
            <div className="row g-3 mb-4">
              <div className="col-4">
                <div className="p-3 text-center rounded-3 bg-light border">
                  <CheckCircle style={{ color: "#2E7D32", fontSize: "28px" }} className="mb-1" />
                  <div className="fw-bold fs-4 text-dark">{counts.SENT || 0}</div>
                  <div className="text-muted" style={{ fontSize: "12px" }}>Delivered / Sent</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 text-center rounded-3 bg-light border">
                  <Error style={{ color: "#ED6C02", fontSize: "28px" }} className="mb-1" />
                  <div className="fw-bold fs-4 text-dark">{counts.FAILED || 0}</div>
                  <div className="text-muted" style={{ fontSize: "12px" }}>Retrying / Failed</div>
                </div>
              </div>
              <div className="col-4">
                <div className="p-3 text-center rounded-3 bg-light border">
                  <Cancel style={{ color: "#D32F2F", fontSize: "28px" }} className="mb-1" />
                  <div className="fw-bold fs-4 text-dark">{counts.DEAD || 0}</div>
                  <div className="text-muted" style={{ fontSize: "12px" }}>Expired / Dead</div>
                </div>
              </div>
            </div>

            {failures.length > 0 && (
              <div>
                <h6 className="fw-semibold mb-2" style={{ fontSize: "14px" }}>Failure Diagnostics</h6>
                <div className="table-responsive" style={{ maxHeight: "200px" }}>
                  <table className="table table-sm table-bordered">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ fontSize: "12px" }}>Status</th>
                        <th style={{ fontSize: "12px" }}>Error Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failures.map((f, idx) => (
                        <tr key={idx}>
                          <td><span className="badge bg-danger">{f.status}</span></td>
                          <td style={{ fontSize: "12px" }}>{f.lastError || "Device token expired or invalid"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
